import {
    PASSWORD_MAX_LENGTH,
    PASSWORD_MIN_LENGTH,
    PASSWORD_MIN_OPTIONAL_RULES,
    PASSWORD_SPECIAL_CHARS_DISPLAY,
    evaluatePassword
} from "@/util/passwordValidation"

/**
 * Provides shared new-password validation to components.
 *
 * The host component must expose `this.input.password` (the password being typed).
 * See @/util/passwordValidation for the underlying criteria.
 */
export default {
    data() {
        return {
            passwordMinLength: PASSWORD_MIN_LENGTH,
            passwordMaxLength: PASSWORD_MAX_LENGTH,
            passwordMinOptionalRules: PASSWORD_MIN_OPTIONAL_RULES,
            passwordSpecialCharsDisplay: PASSWORD_SPECIAL_CHARS_DISPLAY
        }
    },
    computed: {
        passwordCheck() {
            return evaluatePassword(this.input.password)
        },

        // Mandatory requirement: length. Shown as its own line.
        lengthRequirement() {
            return {
                message: this.$vuetify.lang.t('$vuetify.passwordrequirementsMoreThanTwelveChars'),
                valid: this.passwordCheck.lengthValid
            }
        },

        // Optional requirements (the user must meet at least passwordMinOptionalRules of them),
        // each with its translated message and current valid state.
        optionalRequirements() {
            return this.passwordCheck.optional.map(rule => ({
                key: rule.key,
                message: this.$vuetify.lang.t(`$vuetify.${rule.messageKey}`),
                valid: rule.valid
            }))
        },

        optionalMetCount() {
            return this.passwordCheck.optionalMetCount
        },

        // Whether the password meets the full criteria (length + enough optional rules).
        passwordMeetsRequirements() {
            return this.passwordCheck.isValid
        },

        // Rules for the v-text-field. The max length is enforced silently (also via the
        // field's maxlength attribute) without revealing the maximum to the user.
        passwordRules() {
            return [
                v => !!v || this.$vuetify.lang.t('$vuetify.passwordRequired'),
                v => (v && v.length >= PASSWORD_MIN_LENGTH) || this.$vuetify.lang.t('$vuetify.passwordMinChars'),
                v => (!v || v.length <= PASSWORD_MAX_LENGTH) || this.$vuetify.lang.t('$vuetify.passwordFieldError'),
                v => evaluatePassword(v).optionalValid || this.$vuetify.lang.t('$vuetify.passwordFieldError')
            ]
        }
    }
}
